import { useEffect, useRef, useState } from 'react';

export const useField = <T>(initialValue: T) => {
  const [value, setValue] = useState<T>(initialValue);
  const [error, _setError] = useState<string | null | undefined>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Deferred setters below (auto-clearing an error, delayed value/error updates)
  // schedule a setState to run seconds later. An uncleaned timer keeps its callback —
  // and everything that closure captures — alive past the component's lifetime, and in
  // the jsdom unit workers a late-firing timer has crashed the worker outright (turning
  // a green run red). Track every pending timer, clear them on unmount, and refuse to
  // schedule anything once unmounted so nothing fires past this hook's lifetime.
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const isUnmounted = useRef(false);

  const schedule = (callback: () => void, delayMs: number): void => {
    if (isUnmounted.current) return;
    const id = setTimeout(() => {
      timers.current.delete(id);
      callback();
    }, delayMs);
    timers.current.add(id);
  };

  useEffect(() => {
    isUnmounted.current = false; // StrictMode's simulated remount reuses the same refs
    const pending = timers.current;
    return () => {
      isUnmounted.current = true;
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const setError = (errorMessage: string | undefined | null) => {
    if (errorMessage == null) {
      _setError(errorMessage);
      return;
    }
    _setError(errorMessage);
    schedule(() => _setError(''), 4000);
  };

  const validate = (validator: (val: T) => boolean, errorMessage: string): boolean => {
    if (!validator(value)) {
      setError(errorMessage);
      return false;
    }
    _setError('');
    return true;
  };

  const setTimeoutValue = (val: T) => {
    setLoading(true);
    schedule(() => {
      setValue(val);
      setLoading(false);
    }, 4000);
  };

  const setTimeoutError = (errorMessage: string | undefined) => {
    if (!errorMessage) return;
    setLoading(true);
    schedule(() => {
      setError(errorMessage);
      setLoading(false);
    }, 4000);
  };

  return { value, setValue, setTimeoutValue, error, loading, setLoading, validate, setError, setTimeoutError, schedule };
};
